import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaEnvelopeOpenText,
  FaPaperPlane,
  FaTimes,
  FaExclamationTriangle,
} from "react-icons/fa";
import { SectionCard, MiniStat } from "../SectionCard";
import { useInView } from "../useInView";
import GrapesEditor from "../../../components/GrapesJS/GrapesEditor";
import {
  fetchOrgPcInactivityStatus,
  updateOrgPcInactivitySettings,
  previewOrgPcInactivity,
} from "../api";
import { fmtN, formatRelative } from "../format";
import {
  reasonLabel,
  eligibilityTone,
  daysSinceLabel,
  sendStatusLabel,
  sendStatusTone,
  validateSettingsForm,
  buildSettingsPatch,
} from "../pcInactivity";

const ACCENT = "#6366f1";

function toFormState(settings) {
  return {
    enabled: settings?.enabled === true,
    thresholdDays: settings?.thresholdDays ?? 30,
    cooldownDays: settings?.cooldownDays ?? 30,
    maxSends: settings?.maxSends ?? 3,
    subject: settings?.subject ?? "",
    emailHtml: settings?.emailHtml ?? "",
    emailPlainText: settings?.emailPlainText ?? "",
  };
}

/**
 * Editable configuration modal: enabled toggle, threshold/cooldown, subject,
 * full HTML editor (reusing the Marketing GrapesEditor), recipient preview,
 * and a dry-run preview action.
 */
function ConfigModal({ organizationId, env, status, onClose, onSaved }) {
  const [form, setForm] = useState(() => toFormState(status.settings));
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    const validationErrors = validateSettingsForm(form);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors([]);
    setSaving(true);
    setSaveError(null);
    try {
      const patch = buildSettingsPatch(form);
      const json = await updateOrgPcInactivitySettings(
        organizationId,
        patch,
        env,
      );
      onSaved(json);
      onClose();
    } catch (err) {
      setSaveError(err?.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  }, [form, organizationId, env, onSaved, onClose]);

  const handlePreview = useCallback(async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const json = await previewOrgPcInactivity(organizationId, env);
      setPreview(json?.result || null);
    } catch (err) {
      setPreview({ error: err?.message || "Preview failed" });
    } finally {
      setPreviewing(false);
    }
  }, [organizationId, env]);

  const recipients = status.recipients || [];

  return (
    <div className="oo-modal-overlay" role="dialog" aria-modal="true">
      <div className="oo-modal">
        <div className="oo-modal-head">
          <h3>Re-engagement email · {status.organizationName}</h3>
          <button
            type="button"
            className="oo-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <div className="oo-modal-body">
          <div className="oo-field-row">
            <label className="oo-toggle">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setField("enabled", e.target.checked)}
              />
              <span>
                Automated re-engagement email {form.enabled ? "enabled" : "disabled"}
              </span>
            </label>
          </div>

          <div className="oo-field-grid">
            <div className="oo-field">
              <label>Inactivity threshold (days)</label>
              <input
                type="number"
                min="1"
                value={form.thresholdDays}
                onChange={(e) => setField("thresholdDays", e.target.value)}
              />
            </div>
            <div className="oo-field">
              <label>Cooldown between emails (days)</label>
              <input
                type="number"
                min="1"
                value={form.cooldownDays}
                onChange={(e) => setField("cooldownDays", e.target.value)}
              />
            </div>
            <div className="oo-field">
              <label>Maximum successful emails</label>
              <input
                type="number"
                min="1"
                value={form.maxSends}
                onChange={(e) => setField("maxSends", e.target.value)}
              />
            </div>
          </div>

          <div className="oo-field">
            <label>Email subject</label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => setField("subject", e.target.value)}
              placeholder="Subject line"
            />
          </div>

          <div className="oo-field">
            <label>
              Email body — supports {"{{firstName}}"}, {"{{organizationName}}"},{" "}
              {"{{daysSinceLastRun}}"}, {"{{appUrl}}"}
            </label>
            <div className="oo-editor-wrap">
              <GrapesEditor
                initialContent={form.emailHtml}
                onChange={(html) => setField("emailHtml", html)}
              />
            </div>
          </div>

          <div className="oo-field">
            <label>Plain-text fallback (optional)</label>
            <textarea
              rows={4}
              value={form.emailPlainText}
              onChange={(e) => setField("emailPlainText", e.target.value)}
              placeholder="Plain-text version of the email"
            />
          </div>

          <div className="oo-recipients">
            <div className="oo-recipients-head">
              Recipients ({recipients.length}) — active owners/admins
            </div>
            {recipients.length ? (
              <ul className="oo-recipients-list">
                {recipients.slice(0, 12).map((r) => (
                  <li key={r.email}>
                    {r.name ? `${r.name} · ` : ""}
                    {r.email}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="oo-empty-hint" style={{ textAlign: "left" }}>
                No owner/admin recipients resolved — nothing would be sent.
              </div>
            )}
          </div>

          {errors.length > 0 ? (
            <div className="oo-form-errors">
              {errors.map((err) => (
                <div key={err}>
                  <FaExclamationTriangle /> {err}
                </div>
              ))}
            </div>
          ) : null}

          {saveError ? (
            <div className="oo-form-errors">
              <div>
                <FaExclamationTriangle /> {saveError}
              </div>
            </div>
          ) : null}

          {preview ? (
            <div className="oo-preview-result">
              {preview.error ? (
                <span className="oo-chip oo-chip--danger">{preview.error}</span>
              ) : (
                <>
                  <span
                    className={`oo-chip oo-chip--${eligibilityTone(preview.reason)}`}
                  >
                    {reasonLabel(preview.reason)}
                  </span>
                  <span className="oo-preview-detail">
                    {preview.wouldSend
                      ? `Would send to ${preview.recipients?.length || 0} recipient(s)`
                      : "Would not send on the next run"}
                  </span>
                </>
              )}
            </div>
          ) : null}
        </div>

        <div className="oo-modal-foot">
          <button
            type="button"
            className="oo-mini-btn"
            onClick={handlePreview}
            disabled={previewing}
          >
            {previewing ? "Previewing…" : "Dry-run preview"}
          </button>
          <div className="oo-modal-foot-right">
            <button type="button" className="oo-mini-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="oo-mini-btn oo-mini-btn--primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Org 360 processing-checklist retention section. Surfaces PC activity metrics
 * (last run, days since, all-time + 30-day run counts), the re-engagement alert
 * state, recipient preview, last send, and an editable email configuration.
 */
export default function PcRetentionSection({ organizationId, env }) {
  const [ref, inView] = useInView();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const json = await fetchOrgPcInactivityStatus(organizationId, env);
      setStatus(json);
    } catch (err) {
      setError(err?.message || "Failed to load processing retention data");
    } finally {
      setLoading(false);
    }
  }, [organizationId, env]);

  useEffect(() => {
    if (inView) load();
  }, [inView, load]);

  const metrics = status?.metrics;
  const settings = status?.settings;
  const eligibility = status?.eligibility;

  const enabledChip = useMemo(() => {
    if (!settings) {
      return null;
    }
    return settings.enabled ? (
      <span className="oo-chip oo-chip--ok">Alerts on</span>
    ) : (
      <span className="oo-chip">Alerts off</span>
    );
  }, [settings]);

  return (
    <SectionCard
      ref={ref}
      title="Processing Retention"
      subtitle="Checklist activity & re-engagement email"
      icon={<FaEnvelopeOpenText />}
      accent={ACCENT}
      loading={(loading || !inView) && !status}
      error={error}
      onRetry={load}
      size="full"
      headerRight={enabledChip}
    >
      {status ? (
        <>
          <div className="oo-ministat-row">
            <MiniStat
              label="Last PC run"
              value={metrics?.lastRunAt ? formatRelative(metrics.lastRunAt) : "Never"}
              accent={ACCENT}
            />
            <MiniStat label="Days since" value={daysSinceLabel(metrics)} />
            <MiniStat
              label="Manual runs (all time)"
              value={fmtN(metrics?.allTimeRunCount || 0)}
            />
            <MiniStat
              label={`Manual runs (${metrics?.windowDays || 30}d)`}
              value={fmtN(metrics?.recentRunCount || 0)}
            />
            <MiniStat
              label={`Loans opened (${metrics?.windowDays || 30}d)`}
              value={fmtN(metrics?.recentLoanOpenedCount || 0)}
            />
          </div>

          <div className="oo-retention-meta">
            {eligibility ? (
              <span className={`oo-chip oo-chip--${eligibilityTone(eligibility.reason)}`}>
                {reasonLabel(eligibility.reason)}
              </span>
            ) : null}
            <span className="oo-chip">
              Threshold: {settings?.thresholdDays ?? 30}d
            </span>
            <span className="oo-chip">
              Sends: {status.successfulSendCount ?? 0}/
              {settings?.maxSends ?? 3}
            </span>
            <span className="oo-chip">
              Recipients: {status.recipientCount ?? 0}
            </span>
            <span className="oo-chip">
              Last sent:{" "}
              {status.lastAlertSentAt
                ? formatRelative(status.lastAlertSentAt)
                : "never"}
            </span>
          </div>

          {status.recentSends?.length ? (
            <div className="oo-send-history">
              <div className="oo-send-history-head">Recent sends</div>
              <table className="oo-send-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Status</th>
                    <th>Recipients</th>
                  </tr>
                </thead>
                <tbody>
                  {status.recentSends.slice(0, 6).map((send) => (
                    <tr key={send.id}>
                      <td>{formatRelative(send.createdAt)}</td>
                      <td>
                        <span
                          className={`oo-chip oo-chip--${sendStatusTone(send.status)}`}
                        >
                          {sendStatusLabel(send.status)}
                          {send.dryRun ? " (dry-run)" : ""}
                        </span>
                      </td>
                      <td>{send.recipientCount ?? send.recipients?.length ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="oo-empty-hint" style={{ textAlign: "left" }}>
              No re-engagement emails have been sent to this org yet.
            </div>
          )}

          <button
            type="button"
            className="oo-mini-btn oo-mini-btn--primary"
            style={{ marginTop: 12 }}
            onClick={() => setModalOpen(true)}
          >
            <FaPaperPlane style={{ fontSize: 11 }} /> Configure re-engagement email
          </button>

          {modalOpen ? (
            <ConfigModal
              organizationId={organizationId}
              env={env}
              status={status}
              onClose={() => setModalOpen(false)}
              onSaved={(json) => setStatus(json)}
            />
          ) : null}
        </>
      ) : null}
    </SectionCard>
  );
}
